/**
 * ErvaPOS - Employees Page
 * CRUD simples de funcionários (Firestore via backend)
 */

import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Loader2, Edit, Users, Save, RefreshCw, TrendingUp, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { employeesAPI, reportsAPI } from '../services/api';

const localDate = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function EmployeesPage() {
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [performanceTotals, setPerformanceTotals] = useState({});
  const [dateFrom, setDateFrom] = useState(localDate);
  const [dateTo, setDateTo] = useState(localDate);
  const [performanceLoading, setPerformanceLoading] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [saving, setSaving] = useState(false);

  const [selected, setSelected] = useState(null);

  const [form, setForm] = useState({
    name: '',
    role: 'Funcionário',
    email: '',
    phone: '',
    nif: '',
    hourly_rate: 0,
    weekly_hours: 40,
    is_active: true,
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await employeesAPI.getAll();
      setEmployees(res.data || []);
    } catch (e) {
      toast.error('Erro ao carregar funcionários');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const loadPerformance = async () => {
    setPerformanceLoading(true);
    try {
      const res = await reportsAPI.getOperatorsPerformance(dateFrom, dateTo);
      setPerformance(res?.data?.operators || []);
      setPerformanceTotals(res?.data?.totals || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao carregar desempenho por operador');
    } finally {
      setPerformanceLoading(false);
    }
  };

  useEffect(() => { loadPerformance(); }, []); // período inicial: hoje

  const openCreate = () => {
    setForm({
      name: '',
      role: 'Funcionário',
      email: '',
      phone: '',
      nif: '',
      hourly_rate: 0,
      weekly_hours: 40,
      is_active: true,
    });
    setShowCreate(true);
  };

  const openEdit = (emp) => {
    setSelected(emp);
    setForm({
      name: emp.name || '',
      role: emp.role || 'Funcionário',
      email: emp.email || '',
      phone: emp.phone || '',
      nif: emp.nif || '',
      hourly_rate: emp.hourly_rate ?? 0,
      weekly_hours: emp.weekly_hours ?? 40,
      is_active: emp.is_active !== false,
    });
    setShowEdit(true);
  };

  const createEmployee = async () => {
    if (!form.name.trim()) { toast.error('Nome é obrigatório'); return; }
    setSaving(true);
    try {
      await employeesAPI.create({
        ...form,
        email: form.email || NoneToNull(),
        phone: form.phone || NoneToNull(),
        nif: form.nif || NoneToNull(),
      });
      toast.success('Funcionário criado');
      setShowCreate(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao criar funcionário');
    } finally {
      setSaving(false);
    }
  };

  const saveEmployee = async () => {
    if (!selected?.id) return;
    if (!form.name.trim()) { toast.error('Nome é obrigatório'); return; }
    setSaving(true);
    try {
      await employeesAPI.update(selected.id, {
        ...form,
        email: form.email || NoneToNull(),
        phone: form.phone || NoneToNull(),
        nif: form.nif || NoneToNull(),
      });
      toast.success('Funcionário atualizado');
      setShowEdit(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao guardar');
    } finally {
      setSaving(false);
    }
  };

  // helper para manter null quando vazio (o backend aceita null)
  function NoneToNull() { return null; }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Manrope']">Funcionários</h1>
          <p className="text-slate-400">Gestão simples de colaboradores</p>
        </div>
        <Button onClick={openCreate} className="bg-emerald-500 hover:bg-emerald-600">
          <Plus className="w-4 h-4 mr-2" />
          Novo Funcionário
        </Button>
      </div>

      <Card className="card-dark">
        <CardHeader className="pb-3">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div><CardTitle className="text-white flex items-center gap-2"><TrendingUp className="w-5 h-5 text-emerald-400" />Desempenho por operador</CardTitle><p className="text-sm text-slate-400 mt-1">Vendas e diferenças registadas nos fechos de caixa.</p></div>
            <div className="flex flex-wrap items-center gap-2"><Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="bg-slate-800 border-slate-700 w-40" /><span className="text-slate-500">a</span><Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="bg-slate-800 border-slate-700 w-40" /><Button onClick={loadPerformance} disabled={performanceLoading} variant="outline" className="border-slate-700"><RefreshCw className={`w-4 h-4 mr-2 ${performanceLoading ? 'animate-spin' : ''}`} />Atualizar</Button></div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4"><div className="bg-slate-800/70 rounded-lg p-3"><p className="text-xs text-slate-400">Vendas</p><p className="text-xl font-bold text-white">{performanceTotals.sales_count || 0}</p></div><div className="bg-slate-800/70 rounded-lg p-3"><p className="text-xs text-slate-400">Total vendido</p><p className="text-xl font-bold text-emerald-400">€{Number(performanceTotals.sales_total || 0).toFixed(2)}</p></div><div className="bg-slate-800/70 rounded-lg p-3"><p className="text-xs text-slate-400">Sobras de caixa</p><p className="text-xl font-bold text-amber-400">€{Number(performanceTotals.cash_over || 0).toFixed(2)}</p></div><div className="bg-slate-800/70 rounded-lg p-3"><p className="text-xs text-slate-400">Faltas de caixa</p><p className="text-xl font-bold text-rose-400">€{Number(performanceTotals.cash_shortage || 0).toFixed(2)}</p></div></div>
          <div className="overflow-x-auto"><Table className="table-dark"><TableHeader><TableRow><TableHead>Operador</TableHead><TableHead className="text-right">N.º vendas</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Venda média</TableHead><TableHead className="text-right">Fechos</TableHead><TableHead className="text-right">Desvio</TableHead></TableRow></TableHeader><TableBody>{performance.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Sem atividade no período selecionado.</TableCell></TableRow> : performance.map(row => <TableRow key={row.operator_id || row.operator_name}><TableCell className="text-white font-medium">{row.operator_name}</TableCell><TableCell className="text-right text-slate-300">{row.sales_count}</TableCell><TableCell className="text-right text-emerald-400 font-semibold">€{Number(row.sales_total).toFixed(2)}</TableCell><TableCell className="text-right text-slate-300">€{Number(row.average_sale).toFixed(2)}</TableCell><TableCell className="text-right text-slate-300">{row.cash_closes}</TableCell><TableCell className={`text-right font-semibold ${row.cash_difference < 0 ? 'text-rose-400' : row.cash_difference > 0 ? 'text-amber-400' : 'text-slate-300'}`}>{row.cash_difference > 0 ? '+' : ''}€{Number(row.cash_difference).toFixed(2)}{row.cash_difference !== 0 && <AlertTriangle className="w-3 h-3 inline ml-1" />}</TableCell></TableRow>)}</TableBody></Table></div>
        </CardContent>
      </Card>

      <Card className="card-dark">
        <CardHeader className="pb-3">
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-400" />
            Lista
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table className="table-dark">
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Cargo</TableHead>
                <TableHead className="hidden md:table-cell">Email</TableHead>
                <TableHead className="hidden md:table-cell">Telefone</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {employees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-slate-500">
                    Ainda não há funcionários. Cria o primeiro.
                  </TableCell>
                </TableRow>
              ) : employees.map((e) => (
                <TableRow key={e.id} className="hover:bg-slate-800/40">
                  <TableCell className="text-white font-medium">{e.name}</TableCell>
                  <TableCell className="text-slate-300">{e.role}</TableCell>
                  <TableCell className="hidden md:table-cell text-slate-400">{e.email || '-'}</TableCell>
                  <TableCell className="hidden md:table-cell text-slate-400">{e.phone || '-'}</TableCell>
                  <TableCell>
                    {e.is_active ? (
                      <Badge className="bg-emerald-500/20 text-emerald-400">Ativo</Badge>
                    ) : (
                      <Badge className="bg-slate-600 text-slate-200">Inativo</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-slate-700"
                      onClick={() => openEdit(e)}
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Editar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Novo Funcionário</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="text-slate-300">Nome</Label>
              <Input className="bg-slate-800 border-slate-700" value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} />
            </div>

            <div>
              <Label className="text-slate-300">Cargo</Label>
              <Input className="bg-slate-800 border-slate-700" value={form.role}
                onChange={(e) => setForm(prev => ({ ...prev, role: e.target.value }))} />
            </div>

            <div>
              <Label className="text-slate-300">NIF</Label>
              <Input className="bg-slate-800 border-slate-700" value={form.nif}
                onChange={(e) => setForm(prev => ({ ...prev, nif: e.target.value }))} />
            </div>

            <div>
              <Label className="text-slate-300">Email</Label>
              <Input className="bg-slate-800 border-slate-700" value={form.email}
                onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))} />
            </div>

            <div>
              <Label className="text-slate-300">Telefone</Label>
              <Input className="bg-slate-800 border-slate-700" value={form.phone}
                onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))} />
            </div>

            <div>
              <Label className="text-slate-300">€/hora</Label>
              <Input type="number" className="bg-slate-800 border-slate-700" value={form.hourly_rate}
                onChange={(e) => setForm(prev => ({ ...prev, hourly_rate: Number(String(e.target.value || 0).replace(',', '.')) }))} />
            </div>

            <div>
              <Label className="text-slate-300">Horas semana</Label>
              <Input type="number" className="bg-slate-800 border-slate-700" value={form.weekly_hours}
                onChange={(e) => setForm(prev => ({ ...prev, weekly_hours: Number(String(e.target.value || 40).replace(',', '.')) }))} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" className="border-slate-700" onClick={() => setShowCreate(false)}>
              Cancelar
            </Button>
            <Button onClick={createEmployee} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Criar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Editar Funcionário</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="text-slate-300">Nome</Label>
              <Input className="bg-slate-800 border-slate-700" value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} />
            </div>

            <div>
              <Label className="text-slate-300">Cargo</Label>
              <Input className="bg-slate-800 border-slate-700" value={form.role}
                onChange={(e) => setForm(prev => ({ ...prev, role: e.target.value }))} />
            </div>

            <div>
              <Label className="text-slate-300">NIF</Label>
              <Input className="bg-slate-800 border-slate-700" value={form.nif}
                onChange={(e) => setForm(prev => ({ ...prev, nif: e.target.value }))} />
            </div>

            <div>
              <Label className="text-slate-300">Email</Label>
              <Input className="bg-slate-800 border-slate-700" value={form.email}
                onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))} />
            </div>

            <div>
              <Label className="text-slate-300">Telefone</Label>
              <Input className="bg-slate-800 border-slate-700" value={form.phone}
                onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))} />
            </div>

            <div>
              <Label className="text-slate-300">€/hora</Label>
              <Input type="number" className="bg-slate-800 border-slate-700" value={form.hourly_rate}
                onChange={(e) => setForm(prev => ({ ...prev, hourly_rate: Number(String(e.target.value || 0).replace(',', '.')) }))} />
            </div>

            <div>
              <Label className="text-slate-300">Horas semana</Label>
              <Input type="number" className="bg-slate-800 border-slate-700" value={form.weekly_hours}
                onChange={(e) => setForm(prev => ({ ...prev, weekly_hours: Number(String(e.target.value || 40).replace(',', '.')) }))} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" className="border-slate-700" onClick={() => setShowEdit(false)}>
              Cancelar
            </Button>
            <Button onClick={saveEmployee} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
